/**
 * 离线覆盖判定 —— "哪些区域没有缓存"。
 *
 * ## 为什么这是本模块最重要的一件事
 *
 * 野外没网时，**"地图是空白"和"这片区域没缓存"是完全不同的两件事**：
 * 前者说明软件或底图坏了（用户只能放弃），后者说明"往东飞两公里就有图"
 * 或者"回营地前先把这片缓存上"（用户还能继续作业）。浏览器只会给出一张
 * 空白地图，所以这个判断必须由我们自己算，并且要能用中文说清
 * **是哪一片、大概在哪个坐标、要补多少块**。
 *
 * ## 判定在哪个缩放层上做
 *
 * 逐块比对必须在一个"数目可控"的层级上做：z17 的视野可能有几千块瓦片，
 * 而 z15 只要几百块。所以这里从视野的层级出发，**只在数目超标时**降级，
 * 并把降级这件事写进报告（`zoomNote`）—— 悄悄用一个更粗的层级去统计，
 * 会让"报告说已缓存、屏幕上是空白"再次发生。
 */

import { boundsOf, type GeoBounds, type LatLon } from '@hgc/ardupilot';
import {
  coarsestZoomForBounds,
  countTiles,
  tileBounds,
  tileKey,
  tileRangesForBounds,
  tilesInRanges,
  type TileCoord,
} from './tiles';

/** 一次覆盖判定最多逐块比对多少块瓦片。 */
export const DEFAULT_COVERAGE_MAX_TILES = 512;

/** 覆盖层最多画多少块缺失瓦片（再多就只是把地图涂满，没有信息量）。 */
export const DEFAULT_MAX_REPORTED_TILES = 256;

/** 最多列出几处缺失区域。 */
export const DEFAULT_MAX_REGIONS = 5;

/** 缺失区域聚合时向"更粗一层"上溯几个层级（2 层 = 4×4 一块）。 */
export const DEFAULT_REGION_ZOOM_DELTA = 2;

export interface MissingRegion {
  /** 聚合后的瓦片编号（比统计层级更粗） */
  readonly tile: TileCoord;
  readonly bounds: GeoBounds;
  readonly center: LatLon;
  /** 这块区域里有多少块缺失瓦片 */
  readonly tileCount: number;
  /** 统计层级（`region.tile.z` 更粗） */
  readonly zoom: number;
  /** 中文描述，界面直接显示 */
  readonly label: string;
}

export interface CoverageReport {
  readonly sourceId: string;
  /** 统计所用的层级 */
  readonly zoom: number;
  /** 为了把瓦片数降到可控范围而降级的说明；没降级时为 `null` */
  readonly zoomNote: string | null;
  readonly total: number;
  readonly cached: number;
  readonly missing: readonly TileCoord[];
  readonly missingCount: number;
  readonly regions: readonly MissingRegion[];
  /** 统计是否被截断（极端情况下最粗一层仍然超标） */
  readonly partial: boolean;
  /** 是否**完全不缺** */
  readonly complete: boolean;
  /** 面向用户的中文结论 */
  readonly summary: string;
}

export interface CoverageQuery {
  readonly sourceId: string;
  readonly bounds: GeoBounds;
  readonly viewZoom: number;
  readonly sourceZoomRange: { readonly min: number; readonly max: number };
  /** 已缓存的缓存键（`tileKey` 的输出） */
  readonly cachedKeys: ReadonlySet<string>;
  readonly maxTiles?: number;
  readonly maxReportedTiles?: number;
  readonly maxRegions?: number;
  readonly regionZoomDelta?: number;
}

/**
 * 判定统计层级。
 *
 * 上限取 `min(floor(视野缩放), 源的 maxZoom)`：比源更细的层级上**永远**没有瓦片，
 * 在那里统计只会得到"全部缺失"，而那是底图的属性、不是用户该补的东西。
 */
export function coverageZoomFor(query: {
  readonly bounds: GeoBounds;
  readonly viewZoom: number;
  readonly sourceZoomRange: { readonly min: number; readonly max: number };
  readonly maxTiles: number;
}): number {
  const finest = Math.min(
    Math.max(Math.floor(query.viewZoom), query.sourceZoomRange.min),
    query.sourceZoomRange.max,
  );
  return coarsestZoomForBounds(query.bounds, query.maxTiles, {
    min: query.sourceZoomRange.min,
    max: finest,
  });
}

export function computeCoverage(query: CoverageQuery): CoverageReport {
  const maxTiles = query.maxTiles ?? DEFAULT_COVERAGE_MAX_TILES;
  const maxReportedTiles = query.maxReportedTiles ?? DEFAULT_MAX_REPORTED_TILES;
  const maxRegions = query.maxRegions ?? DEFAULT_MAX_REGIONS;
  const regionZoomDelta = query.regionZoomDelta ?? DEFAULT_REGION_ZOOM_DELTA;

  const zoom = coverageZoomFor({
    bounds: query.bounds,
    viewZoom: query.viewZoom,
    sourceZoomRange: query.sourceZoomRange,
    maxTiles,
  });
  const ranges = tileRangesForBounds(query.bounds, zoom);
  const all = tilesInRanges(ranges, maxTiles);
  const total = countTiles(ranges);
  const partial = total > all.length;

  const missing: TileCoord[] = [];
  let cached = 0;
  for (const tile of all) {
    if (query.cachedKeys.has(tileKey(query.sourceId, tile))) cached += 1;
    else if (missing.length < maxReportedTiles) missing.push(tile);
  }
  const missingCount = total - cached;

  const requestedZoom = Math.max(Math.floor(query.viewZoom), query.sourceZoomRange.min);
  const zoomNote =
    zoom < requestedZoom
      ? `当前视野在 z${requestedZoom} 上超过 ${maxTiles} 块瓦片，本次按 z${zoom} 统计`
      : null;

  const regions = groupMissingRegions(missing, zoom, regionZoomDelta, maxRegions);

  return {
    sourceId: query.sourceId,
    zoom,
    zoomNote,
    total,
    cached,
    missing,
    missingCount,
    regions,
    partial,
    complete: missingCount === 0,
    summary: summarizeCoverage({ total, cached, missingCount, regions, partial }),
  };
}

/**
 * 把缺失瓦片按更粗的层级聚合，并给出每处的中心坐标。
 *
 * 直接列出几百个瓦片编号对用户毫无意义；"北纬 31.22°、东经 121.47° 一带缺 48 块"
 * 才是可以照着行动的信息 —— 用户可以飞到那里、或者开着网络把这一片缓存上。
 */
export function groupMissingRegions(
  tiles: readonly TileCoord[],
  zoom: number,
  zoomDelta = DEFAULT_REGION_ZOOM_DELTA,
  maxRegions = DEFAULT_MAX_REGIONS,
): readonly MissingRegion[] {
  if (tiles.length === 0) return [];
  const groupZoom = Math.max(0, zoom - Math.max(0, zoomDelta));
  const shift = zoom - groupZoom;
  const counts = new Map<string, { readonly tile: TileCoord; count: number }>();

  for (const tile of tiles) {
    const group: TileCoord = { z: groupZoom, x: tile.x >> shift, y: tile.y >> shift };
    const key = `${group.z}/${group.x}/${group.y}`;
    const existing = counts.get(key);
    if (existing === undefined) counts.set(key, { tile: group, count: 1 });
    else existing.count += 1;
  }

  return [...counts.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, maxRegions)
    .map(({ tile, count }) => {
      const bounds = tileBounds(tile);
      const center: LatLon = {
        latDeg: (bounds.southDeg + bounds.northDeg) / 2,
        lonDeg: (bounds.westDeg + bounds.eastDeg) / 2,
      };
      return {
        tile,
        bounds,
        center,
        tileCount: count,
        zoom,
        label: `${formatLatLon(center)} 一带（缺 ${count} 块，按 z${zoom} 计）`,
      };
    });
}

function summarizeCoverage(report: {
  readonly total: number;
  readonly cached: number;
  readonly missingCount: number;
  readonly regions: readonly MissingRegion[];
  readonly partial: boolean;
}): string {
  if (report.total === 0) return '当前视野没有需要判定的瓦片';
  const head = `当前视野共 ${report.total} 块瓦片，已缓存 ${report.cached} 块`;
  const tail = report.partial ? '（统计已截断）' : '';
  if (report.missingCount === 0) return `${head}：全部可用${tail}`;
  const first = report.regions[0];
  const where = first === undefined ? '' : `，最集中的一处是 ${first.label}`;
  return `${head}；${report.missingCount} 块无缓存${where}${tail}`;
}

/** 中心点 → 中文经纬度（"北纬 31.22°、东经 121.47°"）。 */
export function formatLatLon(point: LatLon): string {
  const lat = `${point.latDeg >= 0 ? '北纬' : '南纬'} ${Math.abs(point.latDeg).toFixed(2)}°`;
  const lon = `${point.lonDeg >= 0 ? '东经' : '西经'} ${Math.abs(point.lonDeg).toFixed(2)}°`;
  return `${lat}、${lon}`;
}

export interface PrefetchPlan {
  /** 需要下载的瓦片（已剔除缓存里有的） */
  readonly tiles: readonly TileCoord[];
  /** 因为已缓存而跳过的数量 */
  readonly skippedCached: number;
  /** 视野在该层级下共有多少块 */
  readonly total: number;
  /** 是否被 `limit` 截断 */
  readonly truncated: boolean;
  readonly zoom: number;
}

/**
 * 预缓存清单。
 *
 * 与覆盖判定用的是同一套瓦片编号，因此"报告缺哪些"和"去抓哪些"必然一致 ——
 * 两处各写一份的话，用户会遇到"点了缓存、缺图提示还在"。
 */
export function planPrefetch(options: {
  readonly sourceId: string;
  readonly bounds: GeoBounds;
  readonly zoom: number;
  readonly cachedKeys: ReadonlySet<string>;
  readonly limit: number;
}): PrefetchPlan {
  const ranges = tileRangesForBounds(options.bounds, options.zoom);
  const total = countTiles(ranges);
  const all = tilesInRanges(ranges, options.limit);
  const tiles: TileCoord[] = [];
  for (const tile of all) {
    if (!options.cachedKeys.has(tileKey(options.sourceId, tile))) tiles.push(tile);
  }
  return {
    tiles,
    skippedCached: all.length - tiles.length,
    total,
    // 注意两个截断源：limit 本身被用满，以及剔除已缓存前就已经截断
    truncated: tiles.length >= options.limit || all.length < total,
    zoom: options.zoom,
  };
}

/** 一组瓦片的包围盒（用于"把这块缓存上"之后把视野框回原处）。 */
export function boundsOfTiles(tiles: readonly TileCoord[]): GeoBounds | undefined {
  const corners: LatLon[] = [];
  for (const tile of tiles) {
    const bounds = tileBounds(tile);
    corners.push({ latDeg: bounds.southDeg, lonDeg: bounds.westDeg });
    corners.push({ latDeg: bounds.northDeg, lonDeg: bounds.eastDeg });
  }
  return boundsOf(corners);
}

/** 字节数 → 中文可读（缓存占用显示用）。 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unitIndex] ?? 'KB'}`;
}
